import {
  getJobSeekerIdentityQuery,
  getReferralRewardsQuery,
  getReferredJobSeekersQuery,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { humanizeEnum } from '@helpers/labels';
import {
  CHECK_LABELS,
  REWARD_STATUS_LABELS,
  orderedChecks,
  type ReferralCheck,
  type ReferredSeekerRow,
  type RewardRow,
} from './review';

/**
 * One referred job seeker, composed from the queries that exist.
 *
 * The backend has no per-person referral query: `getReferredJobSeekers` searches by name, email
 * or code, and `getReferralRewards` filters by status only. So the record is assembled in three
 * steps, all Super Admin reads the Referrals page already makes:
 *
 * 1. `getJobSeekerById` for the person's email (the URL carries only their id);
 * 2. `getReferredJobSeekers(search: email)`, picking the row whose id matches, since the search is
 *    a substring match and can return neighbours;
 * 3. `getReferralRewards` (all statuses, newest first), scanned a page at a time for rewards whose
 *    `referredId` is this person, bounded at REWARD_SCAN_PAGES pages.
 *
 * A `referredId` argument on getReferralRewards would make step 3 one call; until it exists the
 * scan says when it stopped short rather than claiming there is no reward.
 */

export const REWARD_SCAN_SIZE = 200;
export const REWARD_SCAN_PAGES = 5;
const SEEKER_SEARCH_SIZE = 200;

export interface ReferredPersonRecord {
  /** Name and email from the job seeker record, or null when there is no such job seeker. */
  seeker: { name: string | null; email: string | null } | null;
  /** Their referral row, or null when they did not sign up through a link. */
  person: ReferredSeekerRow | null;
  rewards: RewardRow[];
  /** False when the reward scan hit its page bound without reaching the end of the list. */
  rewardsComplete: boolean;
}

type Fetch = (doc: string) => Promise<any>;

const fullName = (pd: any): string | null => {
  if (!pd) return null;
  const n = (pd.fullName && String(pd.fullName).trim()) || `${pd.firstName ?? ''} ${pd.lastName ?? ''}`.trim();
  return n || null;
};

export const pickReferredRow = (items: ReferredSeekerRow[] | null | undefined, id: string): ReferredSeekerRow | null =>
  (items ?? []).find((r) => r?.jobSeekerId === id) ?? null;

export const rewardsForPerson = (items: RewardRow[] | null | undefined, id: string): RewardRow[] =>
  (items ?? []).filter((r) => r?.referredId === id);

export async function loadReferredPerson(id: string, fetch: Fetch = fetchGraphQLApiData): Promise<ReferredPersonRecord> {
  const js = (await fetch(getJobSeekerIdentityQuery(id)))?.data?.getJobSeekerById;
  if (!js) return { seeker: null, person: null, rewards: [], rewardsComplete: true };
  const seeker = { name: fullName(js.personalDetails), email: js.personalDetails?.email ?? null };

  const search = seeker.email || seeker.name || '';
  let person: ReferredSeekerRow | null = null;
  if (search) {
    const page = (await fetch(getReferredJobSeekersQuery('', '', search, 0, SEEKER_SEARCH_SIZE)))?.data?.getReferredJobSeekers;
    person = pickReferredRow(page?.items, id);
  }
  // A reward is only ever created when someone qualifies, so nobody else needs the scan.
  if (!person || !person.qualifiedAt) return { seeker, person, rewards: [], rewardsComplete: true };

  const rewards: RewardRow[] = [];
  for (let p = 0; p < REWARD_SCAN_PAGES; p += 1) {
    const page = (await fetch(getReferralRewardsQuery('', p, REWARD_SCAN_SIZE)))?.data?.getReferralRewards;
    const items: RewardRow[] = page?.items ?? [];
    rewards.push(...rewardsForPerson(items, id));
    const end = items.length < REWARD_SCAN_SIZE || (p + 1) * REWARD_SCAN_SIZE >= (page?.total ?? 0);
    // One referrer per person, so one reward: once found, the rest of the list cannot add another.
    if (rewards.length > 0 || end) return { seeker, person, rewards, rewardsComplete: true };
  }
  return { seeker, person, rewards, rewardsComplete: false };
}

/** What the person is still waiting on, from getReferredJobSeekers.pendingReason. */
export const PENDING_REASON_LABELS: Record<string, string> = {
  NEEDS_PHONE: 'Needs to add a phone number',
  NEEDS_EMAIL: 'Needs to verify their email',
  NEEDS_APPLICATION: 'Needs to apply to a job',
  NEEDS_PROFILE: 'Needs to complete their profile',
  NEEDS_CV: 'Needs to upload a CV',
  CHECKING_CV: 'CV is being checked',
  NOT_FIT: 'CV does not show our industries',
  SELF: 'Self referral, not eligible',
  PHONE_ALREADY_REWARDED: 'This phone number was rewarded before',
  OUTSIDE_INDIA: 'Lives outside India, not eligible',
  REFERRER_OUTSIDE_INDIA: 'Referrer is outside India, not eligible',
};

export const pendingReasonLabel = (code: string | null | undefined): string | null =>
  code ? PENDING_REASON_LABELS[code] ?? humanizeEnum(code) : null;

/** CRMStatusChip keys, so the stage takes the console's own colours. */
export interface Stage {
  label: string;
  tone: string;
}

/** Where this person stands, reward first: once a reward exists, it is the state that matters. */
export const personStage = (person: ReferredSeekerRow | null, reward: RewardRow | null | undefined): Stage => {
  if (reward) {
    const tone = { PENDING_REVIEW: 'needsreview', EARNED: 'success', PAID: 'success', REJECTED: 'declined' }[reward.status] ?? 'pending';
    return { label: REWARD_STATUS_LABELS[reward.status] ?? humanizeEnum(reward.status), tone };
  }
  if (!person) return { label: 'Not referred', tone: 'inactive' };
  if (person.qualifiedAt) return { label: 'Qualified', tone: 'success' };
  return { label: 'Not yet qualified', tone: 'pending' };
};

export interface GateRow {
  name: string;
  label: string;
  /** null when the backend recorded nothing for this gate. */
  passed: boolean | null;
}

/** Every known gate in reading order, recorded or not, then any gate the backend added later. */
export const gateRows = (checks: ReferralCheck[] | null | undefined): GateRow[] => {
  const recorded = new Map(orderedChecks(checks).map((c) => [c.name, c.passed]));
  const known = Object.keys(CHECK_LABELS).map((name) => ({
    name,
    label: CHECK_LABELS[name],
    passed: recorded.has(name) ? recorded.get(name) ?? null : null,
  }));
  const extra = orderedChecks(checks)
    .filter((c) => !(c.name in CHECK_LABELS))
    .map((c) => ({ name: c.name, label: c.name, passed: c.passed }));
  return [...known, ...extra];
};

export interface Milestone {
  id: string;
  kind: 'submitted' | 'status';
  text: string;
  at: string;
  by?: string | null;
}

/** Signed up, qualified, reviewed, paid: the moments the records date, newest first. */
export const referralMilestones = (person: ReferredSeekerRow | null, rewards: RewardRow[]): Milestone[] => {
  const out: Milestone[] = [];
  if (person?.joinedAt) {
    out.push({ id: 'joined', kind: 'submitted', text: `Signed up with code ${person.code}`, at: person.joinedAt });
  }
  if (person?.qualifiedAt) out.push({ id: 'qualified', kind: 'status', text: 'Qualified', at: person.qualifiedAt });
  for (const r of rewards) {
    if (r.earnedAt) out.push({ id: `${r.id}-earned`, kind: 'status', text: 'Reward created, waiting for review', at: r.earnedAt });
    if (r.reviewedAt) {
      out.push({
        id: `${r.id}-reviewed`,
        kind: 'status',
        text: r.status === 'REJECTED' ? `Reward rejected${r.rejectedReason ? `: ${r.rejectedReason}` : ''}` : 'Reward approved',
        at: r.reviewedAt,
        by: r.reviewedBy,
      });
    }
    if (r.paidAt) {
      out.push({
        id: `${r.id}-paid`,
        kind: 'status',
        text: `Reward paid${r.paymentRef ? `, ref ${r.paymentRef}` : ''}`,
        at: r.paidAt,
        by: r.paidBy,
      });
    }
  }
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
};
